import { createStore, createEffect, createEvent, combine } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { CardsApi } from '@shared/api/rest';
import type { CardLimitsResponse, UpdateLimitCardRequest } from '@shared/api/rest/cards';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';

const getCardLimitsEffect = createEffect((params: IQueryParams<number>) => CardsApi.getLimits(params.request, params.signal));
const updateLimitCardEffect = createEffect((request: UpdateLimitCardRequest) => CardsApi.updateLimitCard(request));
const clearEvent = createEvent();

const $dataStore = createStore<CardLimitsResponse | null>(null)
  .on(getCardLimitsEffect.done, (current, data) => data.result.data || current)
  .on(updateLimitCardEffect.done, (current, data) => data.result.data || current)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getCardLimitsEffect.pending, (_, data) => data)
  .on(getCardLimitsEffect.fail, () => false)
  .on(getCardLimitsEffect.done, () => false)
  .reset(clearEvent);

const $actionLoadingStore = createStore<boolean>(false)
  .on(updateLimitCardEffect.pending, (_, data) => data)
  .on(updateLimitCardEffect.fail, () => false)
  .on(updateLimitCardEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getCardLimitsEffect, () => null)
  .on(getCardLimitsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getCardLimitsEffect.done, () => null)
  .on(updateLimitCardEffect, () => null)
  .on(updateLimitCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateLimitCardEffect.done, () => null)
  .reset(clearEvent);

$errorStore.watch((x) => $notificationStore.events.setError(x));

export const $cardLimitsStore = {
  events: {
    getCardLimits: ApiUtils.wrapperApi(getCardLimitsEffect, RequestStrategyEnum.Debounce),
    updateLimitCard: updateLimitCardEffect,
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    actionLoading: $actionLoadingStore,
    error: $errorStore,
  }),
};
